import type { ListObrasQuery, ObraListSummary, ObraStatus, ObraSummary } from '@obras/shared';
import {
  daysBetween,
  formatCnpj,
  formatCurrencyCompact,
  formatDays,
  formatPercent,
  formatRelativeDate,
  OBRA_PENDENCIA_LABELS,
  OBRA_PENDENCIAS,
  OBRA_STATUS_LABELS,
  VISIT_DUE_SOON_DAYS,
} from '@obras/shared';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Bell,
  CalendarClock,
  CalendarOff,
  CircleCheck,
  CircleDot,
  CircleSlash,
  Clock,
  HardHat,
  PauseCircle,
  Plus,
  Search,
  SlidersHorizontal,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { ObraStatusBadge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { SearchInput } from '../components/ui/Field.js';
import {
  Avatar,
  EmptyState,
  ErrorState,
  RefreshingBar,
  Skeleton,
} from '../components/ui/Feedback.js';
import { MultiSelect, SingleSelect, type MultiSelectOption } from '../components/ui/MultiSelect.js';
import { Pagination } from '../components/ui/Pagination.js';
import { ObraFormModal } from '../features/obras/ObraFormModal.js';
import { useObraFilterOptions, useObras } from '../features/obras/api.js';
import { hasObrasSearch, type ObrasSearch } from '../features/obras/search.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';
import { useStoredState } from '../lib/storage.js';

const ICON = { strokeWidth: 1.5 } as const;

const STATUS_OPTIONS: readonly MultiSelectOption[] = (
  ['em_andamento', 'paralisada', 'concluida', 'cancelada'] as const satisfies readonly ObraStatus[]
).map((status) => ({ value: status, label: OBRA_STATUS_LABELS[status] }));

const KIND_OPTIONS: readonly MultiSelectOption[] = [
  { value: 'nova', label: 'Construção nova', detail: 'Obra erguida do zero' },
  { value: 'reforma', label: 'Reforma ou ampliação', detail: 'Intervenção em obra existente' },
];

/** O que pede ação na obra, com a mesma conta e os mesmos nomes do painel. */
const PENDENCIA_OPTIONS: readonly MultiSelectOption[] = OBRA_PENDENCIAS.map((pendencia) => ({
  value: pendencia,
  label: OBRA_PENDENCIA_LABELS[pendencia],
}));

/**
 * Como a lista é ordenada. Rótulo diz o critério, não o campo do banco.
 *
 * A data de cadastro vem primeiro porque é a ordem estável: "com atualizações"
 * muda de posição conforme quem abre e quando abriu, e a lista que se procura
 * duas vezes seguidas precisa estar no mesmo lugar nas duas.
 */
const SORT_OPTIONS: readonly MultiSelectOption[] = [
  { value: 'recent', label: 'Data de cadastro', detail: 'Mais recentes primeiro' },
  { value: 'updates', label: 'Com atualizações', detail: 'Novidades desde sua última visita' },
  { value: 'progress', label: 'Progresso', detail: 'Maior execução financeira primeiro' },
  { value: 'name', label: 'Ordem alfabética', detail: 'Pelo nome da obra' },
  { value: 'next_visit', label: 'Prazo de visita', detail: 'Vencendo primeiro' },
];

type SortOption = NonNullable<ListObrasQuery['sort']>;

/** O recorte da lista: busca, filtros, ordem e página. */
interface ListState {
  readonly search: string;
  readonly statuses: string[];
  readonly fiscalIds: string[];
  readonly companyIds: string[];
  readonly kinds: string[];
  readonly pendencias: string[];
  readonly sort: SortOption;
  readonly page: number;
}

const INITIAL_LIST_STATE: ListState = {
  search: '',
  statuses: [],
  fiscalIds: [],
  companyIds: [],
  kinds: [],
  pendencias: [],
  sort: 'recent',
  page: 1,
};

/**
 * O recorte que veio pela URL vira o estado inteiro da lista, não um acréscimo.
 *
 * Quem clica em "contratos vencidos" no painel quer ver exatamente isso; somar
 * ao que estava guardado — um fiscal, uma busca — devolveria uma lista que não
 * corresponde ao número clicado.
 */
function listStateFromSearch(search: ObrasSearch): ListState {
  const csv = (value: string | undefined): string[] => (value ? value.split(',') : []);

  return restoreListState({
    ...INITIAL_LIST_STATE,
    pendencias: csv(search.pendencias),
    statuses: csv(search.statuses),
    fiscalIds: csv(search.fiscalIds),
  }) as ListState;
}

/**
 * Reconstrói o recorte guardado no navegador, campo a campo.
 *
 * Cada campo que não bate com o que a tela aceita hoje volta ao inicial sem
 * derrubar os demais: uma ordem que deixou de existir não deve apagar o filtro
 * de fiscal que estava ao lado.
 */
function restoreListState(raw: unknown): ListState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const saved = raw as Record<string, unknown>;

  const strings = (value: unknown, allowed?: readonly MultiSelectOption[]): string[] =>
    Array.isArray(value)
      ? value.filter(
          (item): item is string =>
            typeof item === 'string' &&
            (allowed === undefined || allowed.some((option) => option.value === item)),
        )
      : [];

  return {
    search: typeof saved.search === 'string' ? saved.search : INITIAL_LIST_STATE.search,
    statuses: strings(saved.statuses, STATUS_OPTIONS),
    fiscalIds: strings(saved.fiscalIds),
    companyIds: strings(saved.companyIds),
    kinds: strings(saved.kinds, KIND_OPTIONS),
    pendencias: strings(saved.pendencias, PENDENCIA_OPTIONS),
    sort: SORT_OPTIONS.some((option) => option.value === saved.sort)
      ? (saved.sort as SortOption)
      : INITIAL_LIST_STATE.sort,
    page:
      typeof saved.page === 'number' && Number.isInteger(saved.page) && saved.page >= 1
        ? saved.page
        : INITIAL_LIST_STATE.page,
  };
}

/** Evita disparar uma requisição por tecla digitada. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

interface FooterMark {
  readonly label: string;
  readonly tone: string;
  readonly icon: LucideIcon;
}

/**
 * O que ocupa o canto direito do rodapé do cartão.
 *
 * Em obra viva a pergunta é o prazo da próxima visita — é o que aciona o
 * fiscal. Em obra encerrada esse prazo não quer dizer nada, e mostrá-lo
 * vencido seria alarme falso: ali o que informa é quando houve o último
 * registro.
 */
function footerMark(obra: ObraSummary): FooterMark {
  const encerrada = obra.status === 'concluida' || obra.status === 'cancelada';

  if (encerrada) {
    return {
      label: obra.lastActivityAt ? formatRelativeDate(obra.lastActivityAt) : 'sem registro',
      tone: 'text-muted-foreground',
      icon: Clock,
    };
  }

  if (obra.visitStatus === 'sem_visita' || !obra.nextVisitDueAt) {
    return { label: 'Sem visita', tone: 'text-muted-foreground', icon: CalendarOff };
  }

  const days = daysBetween(today(), obra.nextVisitDueAt);

  if (days < 0) return { label: 'Visita vencida', tone: 'text-danger', icon: TriangleAlert };
  if (days === 0) return { label: 'Visita hoje', tone: 'text-warning', icon: CalendarClock };

  return {
    label: `Visita em ${formatDays(days)}`,
    tone: days <= VISIT_DUE_SOON_DAYS ? 'text-warning' : 'text-muted-foreground',
    icon: CalendarClock,
  };
}

/**
 * Cartão da obra.
 *
 * Três zonas, na ordem em que a pergunta aparece em campo: **que obra é esta**
 * (foto e nome), **de quem é** (contrato e fiscal) e **como vai** (execução e
 * prazo). O que exige reação — novidade desde a última visita e situação fora
 * do curso normal — vira selo sobre a foto, onde é visto antes de qualquer
 * leitura. Obra em andamento não ganha selo: é o esperado, e etiquetar o
 * esperado só faz a exceção sumir no meio.
 */
function ObraCard({ obra }: { obra: ObraSummary }) {
  const hasUpdates = obra.updatesSinceLastView > 0;
  const mark = footerMark(obra);

  return (
    <Link
      to="/obras/$obraId"
      params={{ obraId: obra.id }}
      className="group flex flex-col overflow-hidden rounded-lg bg-card"
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-muted">
        {obra.coverUrl ? (
          <img
            src={obra.coverUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          />
        ) : (
          <div className="grid size-full place-items-center">
            <HardHat {...ICON} size={22} className="text-muted-foreground" aria-hidden />
          </div>
        )}

        {hasUpdates ? (
          <span className="absolute top-2.5 left-2.5 rounded-xs bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground">
            {obra.updatesSinceLastView} nova{obra.updatesSinceLastView === 1 ? '' : 's'}
          </span>
        ) : null}

        {obra.status !== 'em_andamento' ? (
          <span className="absolute top-2.5 right-2.5">
            <ObraStatusBadge status={obra.status} />
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-3 px-4 pt-3.5 pb-3.5">
        <h3 className="line-clamp-2 text-[15px] leading-snug font-semibold tracking-tight">
          {obra.name}
        </h3>

        <div className="mt-auto flex items-center gap-3 text-[12px] text-muted-foreground">
          <span className="shrink-0 font-mono">{obra.contractNumber ?? 'sem contrato'}</span>

          {obra.responsibleFiscal ? (
            <span className="ml-auto flex min-w-0 items-center gap-1.5">
              <Avatar
                name={obra.responsibleFiscal.name}
                url={obra.responsibleFiscal.avatarUrl}
                size={18}
              />
              <span className="truncate">{obra.responsibleFiscal.name}</span>
            </span>
          ) : (
            <span className="ml-auto">sem fiscal</span>
          )}
        </div>
      </div>

      {/*
       * Faixa de acompanhamento, um tom abaixo do cartão.
       *
       * A barra traz duas leituras sobrepostas: a parte cinza é o prazo de
       * contrato já decorrido, a escura é o quanto foi pago. Escura menor que
       * cinza é obra andando mais devagar que o calendário — a comparação que
       * o fiscal faz de cabeça, feita pelo desenho.
       */}
      <div className="bg-muted px-4 py-3 transition-colors duration-100 group-hover:bg-accent">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
            Execução
          </span>
          <span className="numeral text-[13px]">{formatPercent(obra.executionProgress)}</span>
        </div>

        <div
          className="relative mt-2 h-1 overflow-hidden rounded-full bg-card"
          role="img"
          title={`Prazo decorrido: ${formatPercent(obra.timeProgress)}`}
          aria-label={`Execução ${formatPercent(obra.executionProgress)}, prazo decorrido ${formatPercent(obra.timeProgress)}`}
        >
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-foreground/25"
            style={{ width: `${obra.timeProgress * 100}%` }}
          />
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-foreground"
            style={{ width: `${obra.executionProgress * 100}%` }}
          />
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-3 text-[12px]">
          <span className="tabular truncate text-muted-foreground">
            {formatCurrencyCompact(obra.paidValueCents)} de{' '}
            {formatCurrencyCompact(obra.currentValueCents)}
          </span>
          <span className={cn('flex shrink-0 items-center gap-1.5', mark.tone)}>
            <mark.icon {...ICON} size={13} aria-hidden />
            {mark.label}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ObraCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <Skeleton className="aspect-[16/9] rounded-none" />
      <div className="flex flex-col gap-3 px-4 pt-3.5 pb-3.5">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-[74px] rounded-none" />
    </Card>
  );
}

/**
 * Situações que a faixa de resumo sempre mostra, mesmo zeradas.
 *
 * São as três que descrevem a rotina do órgão; sumir com a paralisada quando
 * ela chega a zero tiraria justamente a informação boa ("nenhuma parada").
 * Planejada e cancelada só aparecem quando existem, para não encompridar a
 * faixa com o que quase nunca é o caso.
 */
const ALWAYS_SHOWN: readonly ObraStatus[] = ['em_andamento', 'paralisada', 'concluida'];

const STAT_STYLE: Record<ObraStatus, { icon: LucideIcon; tone: string }> = {
  em_andamento: { icon: CircleDot, tone: 'text-muted-foreground' },
  paralisada: { icon: PauseCircle, tone: 'text-paused' },
  concluida: { icon: CircleCheck, tone: 'text-success' },
  cancelada: { icon: CircleSlash, tone: 'text-muted-foreground' },
};

const STAT_LABELS: Record<ObraStatus, [string, string]> = {
  em_andamento: ['em andamento', 'em andamento'],
  paralisada: ['paralisada', 'paralisadas'],
  concluida: ['concluída', 'concluídas'],
  cancelada: ['cancelada', 'canceladas'],
};

function Stat({
  icon: Icon,
  tone,
  value,
  label,
}: {
  icon: LucideIcon;
  tone: string;
  value: number | string;
  label: string;
}) {
  return (
    <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
      <Icon {...ICON} size={14} className={cn('shrink-0', tone)} aria-hidden />
      <span className="tabular font-medium text-foreground">{value}</span>
      {label}
    </span>
  );
}

/**
 * Faixa de resumo do recorte em tela.
 *
 * Fica embaixo e à esquerda, contrapeso à barra de filtros, que corre à
 * direita. É leitura, não comando: cada item diz quanto há de cada coisa
 * dentro do que os filtros deixaram passar.
 */
function SummaryStrip({
  summary,
  total,
  showsUpdates,
}: {
  summary: ObraListSummary;
  total: number;
  showsUpdates: boolean;
}) {
  const all = Object.keys(STAT_STYLE) as ObraStatus[];
  const statuses = all.filter(
    (status) => ALWAYS_SHOWN.includes(status) || summary.byStatus[status] > 0,
  );

  /*
   * As parcelas por situação ignoram o filtro de situação, então elas somam o
   * acervo inteiro do recorte, não o que está paginado. Com filtro de situação
   * ligado, `total` seria menor que a soma das parcelas e a faixa se
   * contradiria; dizer "3 de 18" resolve sem esconder nem uma nem outra.
   */
  const universe = all.reduce((sum, status) => sum + summary.byStatus[status], 0);

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      <Stat
        icon={HardHat}
        tone="text-muted-foreground"
        value={total === universe ? total : `${total} de ${universe}`}
        label={universe === 1 ? 'obra' : 'obras'}
      />

      {statuses.map((status) => (
        <Stat
          key={status}
          icon={STAT_STYLE[status].icon}
          tone={STAT_STYLE[status].tone}
          value={summary.byStatus[status]}
          label={STAT_LABELS[status][summary.byStatus[status] === 1 ? 0 : 1]}
        />
      ))}

      {showsUpdates && summary.withUpdates > 0 ? (
        <Stat
          icon={Bell}
          tone="text-primary"
          value={summary.withUpdates}
          label={summary.withUpdates === 1 ? 'com novidade' : 'com novidades'}
        />
      ) : null}
    </div>
  );
}

export function ObrasPage() {
  const { can, may, user, organization } = useAuth();

  /*
   * O recorte fica guardado no navegador, por pessoa e órgão: quem sai para
   * abrir uma obra e volta encontra os mesmos filtros e a mesma página. É a
   * tela em que mais se entra e sai, e refazer o recorte a cada volta era o
   * que mais custava.
   */
  const urlSearch = useSearch({ from: '/protected/obras' });
  const navigate = useNavigate();

  /*
   * Recorte vindo pela URL (do painel) tem precedência sobre o guardado, e é
   * lido uma vez, na montagem: depois de aplicado, a URL é limpa, para que
   * recarregar a tela ou voltar da obra não o reimponha sobre o que a pessoa
   * mudou desde então.
   */
  const [fromUrl] = useState(() =>
    hasObrasSearch(urlSearch) ? listStateFromSearch(urlSearch) : null,
  );
  const [listState, setListState] = useStoredState(
    `obras.lista:${organization?.id ?? '-'}:${user?.id ?? '-'}`,
    fromUrl ?? INITIAL_LIST_STATE,
    (raw) => fromUrl ?? restoreListState(raw),
  );
  const { search, statuses, fiscalIds, companyIds, kinds, pendencias, sort, page } = listState;
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (fromUrl) void navigate({ to: '/obras', search: {}, replace: true });
  }, [fromUrl, navigate]);

  /** Altera um campo do recorte, no formato que cada controle entrega. */
  function setField<K extends keyof ListState>(field: K): (value: ListState[K]) => void {
    return (value) => setListState((current) => ({ ...current, [field]: value }));
  }

  const debouncedSearch = useDebounced(search);
  const options = useObraFilterOptions();

  const filters = useMemo(
    () =>
      ({
        search: debouncedSearch || undefined,
        statuses: statuses as ListObrasQuery['statuses'],
        fiscalIds,
        companyIds,
        kinds: kinds as ListObrasQuery['kinds'],
        pendencias: pendencias as ListObrasQuery['pendencias'],
        sort,
        page,
        perPage: 12,
      }) satisfies Partial<ListObrasQuery>,
    [debouncedSearch, statuses, fiscalIds, companyIds, kinds, pendencias, sort, page],
  );

  const { data, isPending, isFetching, isError, error, refetch } = useObras(filters);

  const activeFilters =
    statuses.length + fiscalIds.length + companyIds.length + kinds.length + pendencias.length;

  /*
   * Filtro novo, primeira página. O ajuste é durante a renderização, e não num
   * efeito: no efeito, o React chegava a montar a lista pedindo a página 4 de
   * um resultado que agora tem uma, e só então corrigia.
   */
  const assinaturaDoFiltro = JSON.stringify([
    debouncedSearch,
    statuses,
    fiscalIds,
    companyIds,
    kinds,
    pendencias,
    sort,
  ]);
  const [assinaturaDoFiltroAnterior, setAssinaturaDoFiltroAnterior] = useState(assinaturaDoFiltro);
  if (assinaturaDoFiltroAnterior !== assinaturaDoFiltro) {
    setAssinaturaDoFiltroAnterior(assinaturaDoFiltro);
    setField('page')(1);
  } else if (data && data.page === page && data.page > data.totalPages) {
    /*
     * A página guardada pode ter deixado de existir: obra excluída, recorte
     * que encolheu desde a última visita. A API responde a página pedida,
     * vazia, e a tela diria "nenhuma obra" sobre um recorte que tem obras.
     * Compara com a página do próprio resultado porque, durante a troca,
     * `data` ainda é o resultado anterior.
     */
    setField('page')(data.totalPages);
  }

  function clearFilters(): void {
    setListState((current) => ({
      ...current,
      statuses: [],
      fiscalIds: [],
      companyIds: [],
      kinds: [],
      pendencias: [],
    }));
  }

  const fiscalOptions: MultiSelectOption[] = (options.data?.fiscais ?? []).map((fiscal) => ({
    value: fiscal.id,
    label: fiscal.name,
  }));

  /*
   * A empresa é buscável por nome fantasia, razão social e CNPJ. O CNPJ entra
   * como termo oculto e formatado: quem procura tem o número no contrato, não
   * o nome exato que foi cadastrado.
   */
  const companyOptions: MultiSelectOption[] = (options.data?.companies ?? []).map((company) => ({
    value: company.id,
    label: company.tradeName ?? company.legalName,
    detail: [
      company.tradeName ? company.legalName : null,
      company.cnpj ? formatCnpj(company.cnpj) : null,
    ]
      .filter(Boolean)
      .join(' · '),
    terms: company.cnpj ? [company.cnpj, formatCnpj(company.cnpj)] : [],
  }));

  return (
    <AppShell>
      <PageHeader
        title="Obras"
        description="Todo o portfólio. Use os filtros para restringir a listagem."
        action={
          may('canCreateObras') ? (
            <Button
              variant="primary"
              icon={<Plus {...ICON} size={16} />}
              onClick={() => setFormOpen(true)}
            >
              Nova obra
            </Button>
          ) : null
        }
      />

      {/* Comando à direita, leitura à esquerda: os dois não disputam a mesma borda. */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {activeFilters > 0 ? (
          <Button variant="ghost" size="sm" className="mr-auto" onClick={clearFilters}>
            Limpar filtros
          </Button>
        ) : null}

        <MultiSelect
          label="Fiscal"
          options={fiscalOptions}
          selected={fiscalIds}
          onChange={setField('fiscalIds')}
          searchable
        />
        <MultiSelect
          label="Tipo"
          options={KIND_OPTIONS}
          selected={kinds}
          onChange={setField('kinds')}
        />
        <MultiSelect
          label="Situação"
          options={STATUS_OPTIONS}
          selected={statuses}
          onChange={setField('statuses')}
        />
        <MultiSelect
          label="Pendência"
          options={PENDENCIA_OPTIONS}
          selected={pendencias}
          onChange={setField('pendencias')}
        />
        <MultiSelect
          label="Empresa"
          options={companyOptions}
          selected={companyIds}
          onChange={setField('companyIds')}
          searchable
        />

        <SingleSelect
          label="Ordenar por"
          options={SORT_OPTIONS}
          value={sort}
          onChange={(value) => setField('sort')(value as SortOption)}
          className="w-52"
        />

        <div className="relative w-full sm:w-64">
          <Search
            {...ICON}
            size={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <SearchInput
            value={search}
            onChange={(event) => setField('search')(event.target.value)}
            placeholder="Buscar obra ou contrato"
            aria-label="Buscar obras"
            className="pr-3 pl-9"
          />
        </div>
      </div>

      <div className="mt-3 mb-5 min-h-[22px] border-t border-border pt-3">
        {data ? (
          <SummaryStrip summary={data.summary} total={data.total} showsUpdates={can('gestor')} />
        ) : null}
      </div>

      {/* Revalidação não desmonta o conteúdo: só sinaliza com a barra fina. */}
      <div className="mb-4">
        <RefreshingBar active={isFetching && !isPending} />
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <ObraCardSkeleton key={index} />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={activeFilters > 0 || search ? SlidersHorizontal : HardHat}
            title={
              activeFilters > 0 || search
                ? 'Nenhuma obra com esses filtros'
                : 'Nenhuma obra cadastrada'
            }
            description={
              activeFilters > 0 || search
                ? 'Remova algum filtro ou revise os termos da busca.'
                : 'Cadastre a primeira obra para começar o acompanhamento.'
            }
            action={
              activeFilters > 0 ? (
                <Button onClick={clearFilters}>Limpar filtros</Button>
              ) : may('canCreateObras') && !search ? (
                <Button
                  variant="primary"
                  icon={<Plus {...ICON} size={16} />}
                  onClick={() => setFormOpen(true)}
                >
                  Nova obra
                </Button>
              ) : null
            }
          />
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {data.items.map((obra) => (
              <ObraCard key={obra.id} obra={obra} />
            ))}
          </div>

          <Pagination page={page} totalPages={data.totalPages} onChange={setField('page')} />
        </>
      )}

      <ObraFormModal open={formOpen} onClose={() => setFormOpen(false)} />
    </AppShell>
  );
}
